import type {
  LogSectionDataInput,
  LogSectionEntryInput,
  LogSectionFieldValueInput,
} from '@/__generated__/graphql';
import {
  ExpressionType,
  MathOperator,
  SectionUnitsEnum,
  TemplateFieldTypeEnum,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import type { QaqcKpiTemplate, QaqcKpiTemplateField } from '../types';
import { convertValueByUnit } from '../utils';

export type QaqcEntryFormModel = {
  entryIndex: number;
  values: Record<string, string | string[]>;
};

export type QaqcTableFormModel = {
  sectionKey: string;
  tableKey: string;
  title: string;
  supportsMultipleEntries: boolean;
  fieldKeys: string[];
  entries: QaqcEntryFormModel[];
};

export type QaqcFormValues = {
  tables: QaqcTableFormModel[];
};

export const normalizeQaqcTableKey = (key: string | null | undefined): string => {
  if (key === null || key === undefined || key === '') {
    return '';
  }

  return key;
};

export const emptyValuesForFieldKeys = (
  fieldKeys: string[],
  fieldByKey: Map<string, QaqcKpiTemplate['fields'][number]>
): Record<string, string | string[]> => {
  const values: Record<string, string | string[]> = {};
  for (const key of fieldKeys) {
    const field = fieldByKey.get(key);
    if (field?.isDerived) {
      values[key] = '';
      continue;
    }
    if (field?.allowsMultipleValues || field?.type === TemplateFieldTypeEnum.MultiSelect) {
      values[key] = [];
    } else {
      values[key] = '';
    }
  }
  return values;
};

export const buildFieldByKey = (template: QaqcKpiTemplate | undefined) => {
  const map = new Map<string, QaqcKpiTemplate['fields'][number]>();
  if (!template?.fields) return map;
  for (const field of template.fields) {
    if (field.key) {
      map.set(field.key, field);
    }
  }
  return map;
};

export const buildEmptyQaqcFormValues = (template: QaqcKpiTemplate | undefined): QaqcFormValues => {
  const tables: QaqcTableFormModel[] = [];
  if (!template?.qaqcConfig?.length) {
    return { tables };
  }

  const fieldByKey = buildFieldByKey(template);
  const sortedSections = [...template.qaqcConfig].sort((a, b) => a.order - b.order);

  for (const section of sortedSections) {
    const sortedTables = [...section.tables].sort((a, b) => a.order - b.order);
    for (const table of sortedTables) {
      const fieldKeys = table.fieldKeys ?? [];
      tables.push({
        sectionKey: section.sectionKey,
        tableKey: table.tableKey,
        title: table.title,
        supportsMultipleEntries: table.supportsMultipleEntries,
        fieldKeys,
        entries: [
          {
            entryIndex: 1,
            values: emptyValuesForFieldKeys(fieldKeys, fieldByKey),
          },
        ],
      });
    }
  }

  return { tables };
};

const parseServerValues = (
  fieldKey: string,
  raw: string[] | undefined,
  fieldByKey: Map<string, QaqcKpiTemplate['fields'][number]>
): string | string[] => {
  const field = fieldByKey.get(fieldKey);
  const list = raw ?? [];
  if (field?.allowsMultipleValues || field?.type === TemplateFieldTypeEnum.MultiSelect) {
    return [...list];
  }
  return list[0] ?? '';
};

/** Same storage rules as KPI logging: area is persisted in m²; form shows m² or ft² from user unit preference. */
const shouldConvertAreaField = (field: QaqcKpiTemplateField | undefined): boolean =>
  Boolean(
    field &&
      !field.isDerived &&
      field.fieldUnit === SectionUnitsEnum.Area &&
      (field.type === TemplateFieldTypeEnum.Numeric || field.type === TemplateFieldTypeEnum.Input)
  );

const convertSingleAreaMetricStringToDisplay = (
  raw: string,
  unitSystem: UnitSystemEnum,
  field: QaqcKpiTemplateField | undefined
): string => {
  if (raw === '' || raw.trim() === '') {
    return raw;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return raw;
  }
  const decimals = field?.roundToDecimal ?? 3;
  const converted = convertValueByUnit(n, SectionUnitsEnum.Area, unitSystem, 'view', {
    viewRoundDecimals: decimals,
  });
  return String(converted);
};

const convertSingleAreaDisplayStringToMetric = (
  raw: string,
  unitSystem: UnitSystemEnum
): string => {
  if (raw === '' || raw.trim() === '') {
    return raw;
  }
  const n = Number(raw);
  if (!Number.isFinite(n)) {
    return raw;
  }
  const converted = convertValueByUnit(n, SectionUnitsEnum.Area, unitSystem, 'update');
  return String(converted);
};

export const convertQaqcStoredValueToFormDisplay = (
  value: string | string[],
  field: QaqcKpiTemplateField | undefined,
  unitSystem: UnitSystemEnum
): string | string[] => {
  if (!shouldConvertAreaField(field)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      convertSingleAreaMetricStringToDisplay(String(item), unitSystem, field)
    );
  }
  return convertSingleAreaMetricStringToDisplay(value, unitSystem, field);
};

export const convertQaqcFormValueToStored = (
  value: string | string[],
  field: QaqcKpiTemplateField | undefined,
  unitSystem: UnitSystemEnum
): string | string[] => {
  if (!shouldConvertAreaField(field)) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => convertSingleAreaDisplayStringToMetric(String(item), unitSystem));
  }
  return convertSingleAreaDisplayStringToMetric(value, unitSystem);
};

export const mergeServerSectionsIntoForm = (
  base: QaqcFormValues,
  template: QaqcKpiTemplate | undefined,
  serverSections: Array<{
    sectionKey: string;
    tableKey?: string | null;
    entries?: Array<{
      entryIndex: number;
      fieldValues?: Array<{ fieldKey: string; values?: string[] | null }>;
    }> | null;
  }> | null,
  unitSystem: UnitSystemEnum
): QaqcFormValues => {
  if (!serverSections?.length || !template) {
    return base;
  }

  const fieldByKey = buildFieldByKey(template);

  return {
    tables: base.tables.map((table) => {
      const match = serverSections.find(
        (section) =>
          section.sectionKey === table.sectionKey &&
          normalizeQaqcTableKey(section.tableKey) === normalizeQaqcTableKey(table.tableKey)
      );

      if (!match?.entries?.length) {
        return table;
      }

      const mergedEntries = match.entries.map((entry) => {
        const values = emptyValuesForFieldKeys(table.fieldKeys, fieldByKey);
        for (const fv of entry.fieldValues ?? []) {
          if (fv.fieldKey) {
            const parsed = parseServerValues(fv.fieldKey, fv.values ?? undefined, fieldByKey);
            values[fv.fieldKey] = convertQaqcStoredValueToFormDisplay(
              parsed,
              fieldByKey.get(fv.fieldKey),
              unitSystem
            );
          }
        }
        return {
          entryIndex: entry.entryIndex,
          values,
        };
      });

      return {
        ...table,
        entries: mergedEntries,
      };
    }),
  };
};

const parseNumericValues = (value: string | string[] | undefined): number[] => {
  let list: string[];
  if (Array.isArray(value)) {
    list = value;
  } else if (value !== undefined && value !== null && value !== '') {
    list = [String(value)];
  } else {
    list = [];
  }
  return list.map((v) => Number.parseFloat(String(v))).filter((n) => Number.isFinite(n));
};

export const averageNumericMultiValue = (value: string | string[] | undefined): string => {
  const nums = parseNumericValues(value);
  if (nums.length === 0) {
    return '';
  }
  const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
  return avg.toFixed(2);
};

type ExpressionSlice = {
  type?: ExpressionType | null;
  operator?: MathOperator | null;
  value?: number | null;
  variable?: string | null;
  args?: Array<ExpressionSlice | null> | null;
};

type QaqcTemplateSliceForDerived = {
  fields?: Array<{
    key?: string | null;
    isDerived?: boolean | null;
    derivedEquationKey?: string | null;
  }> | null;
  equations?: Array<{
    key: string;
    expression?: ExpressionSlice | null;
    variables?: Array<{ name: string; fieldKey: string } | null> | null;
  }> | null;
};

type QaqcEquationSlice = NonNullable<QaqcTemplateSliceForDerived['equations']>[number];

const evaluateExpression = (
  expression: ExpressionSlice,
  variables: Record<string, number>
): number | undefined => {
  switch (expression.type) {
    case ExpressionType.Number:
      return expression.value ?? undefined;
    case ExpressionType.Variable: {
      const variableName = expression.variable ?? '';
      return variables[variableName];
    }
    case ExpressionType.Operation: {
      const expressionArguments: number[] = (expression.args ?? [])
        .filter((arg): arg is ExpressionSlice => arg !== null && arg !== undefined)
        .map((arg) => evaluateExpression(arg, variables))
        .filter((value): value is number => value !== undefined);

      switch (expression.operator) {
        case MathOperator.Count:
          return expressionArguments.length;
        case MathOperator.Add:
          if (expressionArguments.length === 0) return 0;
          return expressionArguments.reduce((a, b) => a + b);
        case MathOperator.Subtract:
          if (expressionArguments.length === 0) return 0;
          return expressionArguments.reduce((a, b) => a - b);
        case MathOperator.Multiply:
          if (expressionArguments.length === 0) return 1;
          return expressionArguments.reduce((a, b) => a * b);
        case MathOperator.Divide:
          if (expressionArguments.length === 0) return undefined;
          return expressionArguments.reduce((a, b) => a / b);
        case MathOperator.Power:
          return expressionArguments.length >= 2
            ? Math.pow(expressionArguments[0], expressionArguments[1])
            : undefined;
        case MathOperator.SquareRoot:
          return expressionArguments.length > 0 && expressionArguments[0] >= 0
            ? Math.sqrt(expressionArguments[0])
            : undefined;
        case MathOperator.Logarithm:
          return expressionArguments.length > 0 && expressionArguments[0] > 0
            ? Math.log(expressionArguments[0])
            : undefined;
        case MathOperator.Sine:
          return expressionArguments.length > 0 ? Math.sin(expressionArguments[0]) : undefined;
        case MathOperator.Cosine:
          return expressionArguments.length > 0 ? Math.cos(expressionArguments[0]) : undefined;
        case MathOperator.Avg:
          return expressionArguments.length > 0
            ? expressionArguments.reduce((a, b) => a + b, 0) / expressionArguments.length
            : undefined;
        case MathOperator.Min:
          return expressionArguments.length > 0 ? Math.min(...expressionArguments) : undefined;
        case MathOperator.Max:
          return expressionArguments.length > 0 ? Math.max(...expressionArguments) : undefined;
        case MathOperator.DailyAvg:
          // DAILY_AVG requires worklog date context — not computable on the client.
          return undefined;
        default:
          return undefined;
      }
    }
    default:
      return undefined;
  }
};

const evaluateEquationFromEntryValues = (
  equation: QaqcEquationSlice | undefined,
  entryValues: Record<string, string | string[]>
): string | undefined => {
  if (!equation?.expression) return undefined;

  const expression = equation.expression;
  if (expression.type !== ExpressionType.Operation || !expression.operator) return undefined;

  // Special case: single-field AVG — average the multi-values of that field
  if (
    expression.operator === MathOperator.Avg &&
    expression.args?.length === 1 &&
    expression.args[0]?.type === ExpressionType.Variable &&
    expression.args[0].variable
  ) {
    const varName = expression.args[0].variable;
    const fieldKey = equation.variables?.find(
      (v) => v?.name === varName || v?.fieldKey === varName
    )?.fieldKey;
    if (!fieldKey) return undefined;
    return averageNumericMultiValue(entryValues[fieldKey]);
  }

  // General case: resolve variables to numbers (sum multi-values) and evaluate.
  // Key by both name and fieldKey — expression.variable may reference either.
  const variables: Record<string, number> = {};
  for (const variableDef of equation.variables ?? []) {
    if (!variableDef) continue;
    const nums = parseNumericValues(entryValues[variableDef.fieldKey]);
    if (nums.length > 0) {
      const value = nums.reduce((a, b) => a + b, 0);
      variables[variableDef.name] = value;
      if (variableDef.fieldKey !== variableDef.name) {
        variables[variableDef.fieldKey] = value;
      }
    }
  }

  const result = evaluateExpression(expression, variables);
  if (typeof result !== 'number' || !Number.isFinite(result)) return undefined;
  return result.toFixed(2);
};

/**
 * Recomputes QA/QC derived field strings for one row from current entry values (unsaved).
 * Evaluates template equations using the same operator logic as the server math engine.
 */
export const computeQaqcEntryDerivedValues = (
  template: QaqcTemplateSliceForDerived,
  entryValues: Record<string, string | string[]>
): Record<string, string> => {
  const result: Record<string, string> = {};
  const equationByKey = new Map(
    (template.equations ?? []).map((equation) => [equation.key, equation])
  );

  for (const field of template.fields ?? []) {
    if (!field.isDerived || !field.key) {
      continue;
    }

    const equationKey = field.derivedEquationKey ?? field.key;
    const equation = equationKey ? equationByKey.get(equationKey) : undefined;
    const computed =
      equationKey === undefined
        ? undefined
        : evaluateEquationFromEntryValues(equation, entryValues);

    if (computed !== undefined) {
      result[field.key] = computed;
    }
  }

  return result;
};

type QaqcLogSectionsLike = {
  sections?: Array<{
    sectionKey: string;
    tableKey?: string | null;
    entries?: Array<{
      entryIndex: number;
      fieldValues?: Array<{ fieldKey: string; values?: string[] | null }>;
    }>;
  }>;
} | null;

type QaqcLogSectionRow = NonNullable<NonNullable<QaqcLogSectionsLike>['sections']>[number];

const isQaqcFieldFilledFromValues = (
  field: QaqcKpiTemplate['fields'][number] | undefined,
  values: string[] | undefined | null
): boolean => {
  const list = values ?? [];
  if (!field) {
    return list.some((value) => String(value).trim() !== '');
  }
  if (field.allowsMultipleValues || field.type === TemplateFieldTypeEnum.MultiSelect) {
    return list.some((value) => String(value).trim() !== '');
  }
  return list.length > 0 && String(list[0]).trim() !== '';
};

const accumulateTableCompletion = (
  fillableKeys: string[],
  entryCount: number,
  fieldByKey: Map<string, QaqcKpiTemplate['fields'][number]>,
  logSection: QaqcLogSectionRow | undefined
): { completed: number; total: number } => {
  let completed = 0;
  const total = fillableKeys.length * entryCount;

  for (let rowIndex = 0; rowIndex < entryCount; rowIndex++) {
    const entry = logSection?.entries?.[rowIndex];
    for (const fieldKey of fillableKeys) {
      const field = fieldByKey.get(fieldKey);
      const fieldValueRow = entry?.fieldValues?.find((row) => row.fieldKey === fieldKey);
      if (isQaqcFieldFilledFromValues(field, fieldValueRow?.values ?? undefined)) {
        completed++;
      }
    }
  }

  return { completed, total };
};

/**
 * Counts completed vs total fillable QA/QC fields for the current log (per shift).
 * Derived template fields are excluded from the total.
 */
export const computeQaqcFieldCompletion = (
  template: QaqcKpiTemplate | undefined,
  log: QaqcLogSectionsLike
): { completed: number; total: number } => {
  if (!template?.qaqcConfig?.length) {
    return { completed: 0, total: 0 };
  }

  const fieldByKey = buildFieldByKey(template);
  const serverSections = log?.sections ?? [];
  let completed = 0;
  let total = 0;

  const sortedSections = [...template.qaqcConfig].sort((a, b) => a.order - b.order);

  for (const section of sortedSections) {
    const sortedTables = [...section.tables].sort((a, b) => a.order - b.order);
    for (const table of sortedTables) {
      const fillableKeys = (table.fieldKeys ?? []).filter((key) => !fieldByKey.get(key)?.isDerived);
      const logSection = serverSections.find(
        (item) =>
          item.sectionKey === section.sectionKey &&
          normalizeQaqcTableKey(item.tableKey) === normalizeQaqcTableKey(table.tableKey)
      );
      const entryCount = Math.max(logSection?.entries?.length ?? 0, 1);
      const row = accumulateTableCompletion(fillableKeys, entryCount, fieldByKey, logSection);
      completed += row.completed;
      total += row.total;
    }
  }

  return { completed, total };
};

export const formValuesToLogSectionInputs = (
  values: QaqcFormValues,
  template: QaqcKpiTemplate | undefined,
  unitSystem: UnitSystemEnum
): LogSectionDataInput[] => {
  const fieldByKey = buildFieldByKey(template);

  return values.tables.map((table) => ({
    sectionKey: table.sectionKey,
    tableKey: normalizeQaqcTableKey(table.tableKey) || undefined,
    title: table.title,
    entries: table.entries.map(
      (entry, index): LogSectionEntryInput => ({
        entryIndex: index + 1,
        fieldValues: table.fieldKeys.flatMap((fieldKey): LogSectionFieldValueInput[] => {
          const raw = entry.values[fieldKey];
          if (raw === undefined || raw === null) {
            return [];
          }
          const field = fieldByKey.get(fieldKey);
          if (Array.isArray(raw)) {
            const converted = convertQaqcFormValueToStored(raw, field, unitSystem);
            const list = Array.isArray(converted) ? converted : [converted];
            return [
              {
                fieldKey,
                values: list.map(String),
              },
            ];
          }
          const string_ = String(raw);
          if (string_ === '') {
            return [];
          }
          const stored = convertQaqcFormValueToStored(string_, field, unitSystem);
          const out = Array.isArray(stored) ? stored[0] : stored;
          return [{ fieldKey, values: [String(out)] }];
        }),
      })
    ),
  }));
};
